import React from "react";
import {
  Box,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Slider,
  Typography,
  useMediaQuery,
  useTheme,
  IconButton,
  styled,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";

const IOSSlider = styled(Slider)(({ theme }) => ({
  color: theme.palette.mode === "dark" ? "#3880ff" : "#007aff",
  height: 4,
  padding: "15px 0",
  "& .MuiSlider-thumb": {
    height: 24,
    width: 24,
    backgroundColor: "#fff",
    boxShadow: "0 0 4px rgba(0, 0, 0, 0.3)",
    "&:focus, &:hover, &.Mui-active": {
      boxShadow: "0 0 8px rgba(0, 122, 255, 0.6)",
    },
  },
 '& .MuiSlider-rail': {
    color: '#d8d8d8',
    opacity: 1,
    height: 3,
    ...theme.applyStyles('dark', {
      color: '#bfbfbf',
      opacity: undefined,
    }),
  },
}));

const DynamicFieldGroup = ({
  title,
  showDropdown = false,
  dropdownOptions = [],
  dropdownValue = "",
  onDropdownChange,
  dropdownLabel = "Select Option",
  showTextField = false,
  textFields = [], // Array of { label, value, onChange, placeholder, ... }
  showSlider = false,
  sliderValue = 0,
  onSliderChange,
  sliderMin = 0,
  sliderMax = 100,
  sliderStep = 1,
  sliderLabel,
  showInputField = false,
  inputValue = "",
  onInputChange,
  inputLabel = "Input",
  inputType = "text",
  showRemoveButton = false,
  onRemove,
  sx = {},
}) => {
  const theme = useTheme();
  const isSmallDevice = useMediaQuery(theme.breakpoints.down("sm"));

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "row",
        flexWrap: isSmallDevice ? "wrap" : "nowrap",
        justifyContent: isSmallDevice ? "flex-evenly" : "space-evenly",
        gap: isSmallDevice ? 0.5 : 1,
        // p: 2,
        // mx: isSmallDevice ? -1 : 2,
        p: isSmallDevice ? 0.5 : 2,
        // borderLeft: "3px solid #87a9e8",
        // border: "1px solid #ccc",
        // borderRadius: 1,
        alignItems: isSmallDevice ? "flex-start" : "center",
        position: "relative",
        marginLeft: isSmallDevice ? "5px" : "0px",
        width: isSmallDevice ? "100%" : "100%",
        maxWidth: isSmallDevice ? "100%" : "100%",
        ...sx,
        // borderBottom: "1px solid red"
      }}
    >
      {/* Title */}
      {title && (
        <Typography
          variant="h6"
          sx={{
            fontWeight: "bold",
            fontSize: { xs: "0.75rem", sm: "0.875rem" },
          }}
        >
          {title}
        </Typography>
      )}

      {/* Dropdown */}
      {showDropdown && (
        <FormControl
          size="small"
          sx={{ minWidth: isSmallDevice ? "80%" : "fit-content" }}
        >
          <Select
            value={dropdownValue}
            onChange={onDropdownChange}
            displayEmpty
            variant="outlined"
            sx={{ border: "1px solid #ccc", borderRadius: 1 }}
          >
            <MenuItem value="">
              <em>{dropdownLabel}</em>
            </MenuItem>
            {dropdownOptions.map((option, index) => (
              <MenuItem key={index} value={option.value || option}>
                {option.label || option}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Text Fields (Array) */}
      {showTextField &&
        textFields.map((field, index) => {

          if (field.type == "display") return (
            <Typography
              key={index}
              variant="body1"
              sx={{
                fontSize: { xs: "0.875rem", sm: "1rem" },
                fontWeight: field.fontWeight || "normal",
                ...{ ...(field?.sx || {}) },
              }}
            >
              {field.value}
            </Typography>
          );
          return <TextField
            key={index}
            id={`standard-${index}`}
            variant={field.variant || "standard"}
            label={field.label}
            value={field.value}
            onChange={field.onChange}
            placeholder={field.placeholder}
            type={field.type || "text"}
            size={field.size || "small"}
            sx={{
              minWidth: isSmallDevice ? "100%" : 180,
              ...{ ...(field?.sx || {}) },
              
            }}
          />
        })}

      {/* Slider */}
      {showSlider && (
        <Box
      sx={{
        flex: 1,
        minWidth: isSmallDevice ? "100%" : 200,
        maxWidth: isSmallDevice ? "100%" : 300,
        px: 2,
      }}
    >
      <Typography variant="body2" sx={{ mb: 1 }}>
        {sliderLabel}
      </Typography>
      <IOSSlider
        value={sliderValue}
        onChange={onSliderChange}
        min={sliderMin}
        max={sliderMax}
        step={sliderStep}
        valueLabelDisplay="auto"
        marks={[
          { value: sliderMin, label: `${sliderMin}` },
          { value: sliderMax, label: `${sliderMax}` },
        ]}
      />
    </Box>
      )}

      {/* Input Field */}
      {showInputField && (
        <TextField
          id="standard-basic"
          label={inputLabel}
          value={inputValue}
          onChange={onInputChange}
          type={inputType}
          size="small"
          variant="standard"
          sx={{
            minWidth: "150px",
            bgcolor: "#fff",
            borderBottom:"0px"
          }}
        />
      )}

      {/* Remove Button */}
      {showRemoveButton && (
        <IconButton
          onClick={onRemove}
          color="error"
          size="small"
          sx={{
            position: isSmallDevice ? "absolute" : "static",
            bottom: isSmallDevice ? -15 : "auto",
            right: isSmallDevice ? -10 : "auto",
          }}
        >
          <DeleteIcon />
        </IconButton>
      )}
    </Box>
  );
};

export default DynamicFieldGroup;
